/**
 * CustomFontColor Plugin for CKEditor
 *
 * This plugin allows users to customize the font color of selected text within the CKEditor. It integrates two core components:
 * - **CustomFontColorEditing**: Manages the logic of editing font color.
 * - **CustomFontColorUI**: Handles the user interface for selecting and applying font colors.
 *
 * ## Features:
 * - **Font Color Editing**:
 *   - Provides functionality for changing the font color of the selected text in the editor.
 *   - Integrates with CKEditor's command system to apply font color changes.
 * - **UI for Font Color Selection**:
 *   - Adds a dropdown or button UI for the user to choose and apply font colors.
 * - **Official Plugin**:
 *   - This plugin is designed as an official CKEditor plugin for easy integration into CKEditor-based applications.
 *
 * ## Usage:
 * The plugin enables users to change the font color of text. It can be used via the UI (a dropdown or button), and integrates seamlessly with CKEditor commands.
 * Example UI integration:
 * ```javascript
 * editor.ui.componentFactory.add(CKPlugins.CustomFontColor, locale => {
 *     // Create dropdown or UI for font color selection
 * });
 * ```
 *
 * ## Configuration:
 * You can configure the available font colors by setting the `options` property in the plugin's configuration.
 * Example:
 * ```javascript
 * editor.config.set(CKPlugins.CustomFontColor, {
 *     fontColorOptions: [...] // List of available font colors
 * });
 * ```
 *
 * ## Implementation Details:
 * - **Font Color Editing**: The `CustomFontColorEditing` class handles the application of font color to selected text.
 * - **UI Integration**: The `CustomFontColorUI` class manages the dropdowns or button views for the user to select and apply font colors.
 * - **CKEditor Command Integration**: The plugin uses CKEditor commands to execute font color changes based on the user's selection.
 *
 * ## References:
 * - CKEditor Plugin API: https://ckeditor.com/docs/ckeditor5/latest/api/module_core_plugin-Plugin.html
 * - CKEditor UI Components: https://ckeditor.com/docs/ckeditor5/latest/api/module_ui_componentfactory-ComponentFactory.html
 *
 * @module CustomFontColor
 * @exports CustomFontColor
 */

import { CustomFontColorEditing } from './adapters/fontcolorEditing';
import { CustomFontColorUI } from './adapters/fontcolorUI';

const Plugin = window.ClassicEditor.Plugin;

export default class CustomFontColor extends Plugin {
    static get pluginName() {
		return 'CustomFontColor';
	}

	static get requires() {
		return [CustomFontColorEditing, CustomFontColorUI];
	}

	static get isOfficialPlugin() {
		return true;
	}
}
